import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CloudUpload, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { AdminShell } from '@/components/admin-shell';
import { FgCard } from '@/components/fg-card';
import { FgButton } from '@/components/fg-button';
import { FgInput } from '@/components/fg-input';
import { FgToggle } from '@/components/fg-toggle';
import { FgToast } from '@/components/fg-toast';
import { FgDialog } from '@/components/fg-dialog';
import { FgSearch } from '@/components/fg-search';
import { SortableList, DragHandle } from '@/components/sortable-list';
import { api, API_BASE } from '@/lib/api';

interface Level {
  id: string;
  name: string;
  displayName: string;
}

interface Lesson {
  id: string;
  levelId: string;
  title: string;
  description: string | null;
  orderIndex: number;
  isFreePreview: boolean;
  encodingStatus: 'pending' | 'encoding' | 'complete' | 'failed';
  videoHlsPath: string | null;
  encodingError: string | null;
}

const STATUS_COLOR: Record<Lesson['encodingStatus'], string> = {
  pending: 'bg-gray-200 text-gray-700',
  encoding: 'bg-yellow-100 text-yellow-700',
  complete: 'bg-success-bg text-success',
  failed: 'bg-error-bg text-error',
};

export function LessonsPage() {
  const qc = useQueryClient();
  const [levelId, setLevelId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Lesson | null>(null);
  const [deleting, setDeleting] = useState<Lesson | null>(null);
  const [optimisticOrder, setOptimisticOrder] = useState<Lesson[] | null>(null);

  const levels = useQuery<Level[]>({
    queryKey: ['levels'],
    queryFn: async () => (await api.get('/api/levels')).data,
  });

  if (!levelId && levels.data && levels.data.length > 0) {
    setLevelId(levels.data[0].id);
  }

  const lessons = useQuery<Lesson[]>({
    queryKey: ['lessons', levelId],
    enabled: !!levelId,
    queryFn: async () => (await api.get(`/api/lessons/by-level/${levelId}`)).data,
  });

  // Reset the optimistic order whenever the level or the server data changes.
  useEffect(() => {
    setOptimisticOrder(null);
  }, [levelId, lessons.data]);

  const filtered = useMemo(() => {
    const source = optimisticOrder ?? lessons.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return source;
    return source.filter(
      (l) => l.title.toLowerCase().includes(q) || (l.description ?? '').toLowerCase().includes(q),
    );
  }, [lessons.data, optimisticOrder, search]);

  const patchLesson = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<Lesson> }) =>
      (await api.patch(`/api/lessons/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons', levelId] });
      FgToast.success('Saved');
      setEditing(null);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Update failed'),
  });

  const deleteLesson = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/lessons/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons', levelId] });
      FgToast.success('Lesson deleted');
      setDeleting(null);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? 'Delete failed'),
  });

  const reorder = useMutation({
    mutationFn: async (ordered: { id: string; orderIndex: number }[]) =>
      (await api.post('/api/lessons/reorder', ordered)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons', levelId] });
      setOptimisticOrder(null);
    },
    onError: (e) => {
      FgToast.error((e as { message?: string }).message ?? 'Reorder failed');
      setOptimisticOrder(null);
    },
  });

  function persistReorder(next: Lesson[]) {
    setOptimisticOrder(next);
    reorder.mutate(
      next.map((l, i) => ({ id: l.id, orderIndex: i + 1 })),
    );
  }

  return (
    <AdminShell>
      <FgCard className="p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
          <div>
            <label className="text-xs font-semibold text-gray-700">Level</label>
            <select
              value={levelId}
              onChange={(e) => {
                setLevelId(e.target.value);
                setSearch('');
              }}
              className="mt-1 h-12 w-full rounded-md border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:border-red-500 focus:outline-none"
            >
              <option value="">— select —</option>
              {levels.data?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} · {l.displayName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 sm:w-72">
            <label className="text-xs font-semibold text-gray-700">Search lessons</label>
            <FgSearch
              placeholder="Filter by title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
              disabled={!levelId}
            />
          </div>
        </div>
      </FgCard>

      {levelId && (
        <LessonUpload
          levelId={levelId}
          existingCount={lessons.data?.length ?? 0}
          onUploaded={() => qc.invalidateQueries({ queryKey: ['lessons', levelId] })}
        />
      )}

      <div className="mt-6">
        {filtered.length === 0 && levelId && (
          <p className="rounded-md bg-[#0A0A0A] p-4 text-sm text-gray-500">
            {search ? `No lessons match "${search}".` : 'No lessons yet — upload one above.'}
          </p>
        )}
        {filtered.length > 0 && (
          <SortableList
            items={filtered}
            onReorder={(next) => {
              // Only re-sort if the user is reordering within the same search-filter
              // view — otherwise we drop an entry the user can't see.
              const visibleIds = new Set(filtered.map((l) => l.id));
              const nextIds = new Set(next.map((l) => l.id));
              if (!visibleIds || visibleIds.size !== nextIds.size) {
                // Merge: keep the new order for visible items, append hidden items unchanged.
                const hidden = (optimisticOrder ?? lessons.data ?? []).filter(
                  (l) => !nextIds.has(l.id),
                );
                persistReorder([...next, ...hidden]);
                return;
              }
              persistReorder(next);
            }}
            renderItem={(l, handleProps) => (
              <LessonRow
                lesson={l}
                onToggleFree={(v) => patchLesson.mutate({ id: l.id, body: { isFreePreview: v } })}
                onRefresh={() => lessons.refetch()}
                onEdit={() => setEditing(l)}
                onDelete={() => setDeleting(l)}
                dragHandle={<DragHandle listeners={handleProps.listeners} attributes={handleProps.attributes} />}
                isDragging={reorder.isPending}
              />
            )}
          />
        )}
      </div>

      {editing && (
        <LessonDialog
          lesson={editing}
          busy={patchLesson.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(body) => patchLesson.mutate({ id: editing.id, body })}
        />
      )}

      {deleting && (
        <FgDialog
          open
          tone="danger"
          title={`Delete "${deleting.title}"?`}
          description="The lesson and any exercises attached to it will be removed. HLS segments on disk in storage/videos/hls/ will leak (janitor job is Phase 9 work)."
          onClose={() => setDeleting(null)}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setDeleting(null)}>
                Cancel
              </FgButton>
              <FgButton
                variant="primary"
                loading={deleteLesson.isPending}
                onClick={() => deleteLesson.mutate(deleting.id)}
              >
                <Trash2 size={14} /> Delete
              </FgButton>
            </>
          }
        >
          <div className="rounded-md border border-warning bg-warning-bg p-3 text-sm text-black">
            ⚠ Lesson video + any attached exercises will be deleted (cascade).
          </div>
        </FgDialog>
      )}
    </AdminShell>
  );
}

function LessonUpload({
  levelId,
  existingCount,
  onUploaded,
}: {
  levelId: string;
  existingCount: number;
  onUploaded: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [title, setTitle] = useState('');
  const [orderIndex, setOrderIndex] = useState(1);
  const [isFreePreview, setIsFreePreview] = useState(existingCount === 0);
  const [file, setFile] = useState<File | null>(null);

  async function createAndUpload() {
    if (!file) return FgToast.error('Pick an MP4 first');
    setPending(true);
    try {
      // 1. Create the lesson in pending state.
      const { data: lesson } = await api.post('/api/lessons', {
        levelId,
        title: title || file.name.replace(/\.[^.]+$/, ''),
        orderIndex,
        isFreePreview,
      });
      // 2. Upload the MP4 to /api/lessons/:id/upload (synchronous encode).
      await uploadWithProgress(`${API_BASE}/api/lessons/${lesson.id}/upload`, file);
      FgToast.success('Upload + encode complete');
      onUploaded();
      // Reset for the next upload
      setTitle('');
      setFile(null);
    } catch (e) {
      FgToast.error((e as { message?: string }).message ?? 'Upload failed');
    } finally {
      setPending(false);
    }
  }

  return (
    <FgCard className="mt-6 p-5">
      <h2 className="text-base font-semibold">Add a new lesson</h2>
      <p className="mt-1 text-xs text-gray-600">
        Video is encoded synchronously in dev (3 HLS ladders + watermark). 480p-first
        uploads finish in a few seconds; full 1080p can take a few minutes.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FgInput
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Greetings & the alphabet"
        />
        <FgInput
          label="Order index"
          type="number"
          value={String(orderIndex)}
          onChange={(e) => setOrderIndex(Number(e.target.value))}
        />
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-700">MP4 file</label>
          <input
            type="file"
            accept="video/mp4"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="mt-1 h-12 rounded-md border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm file:mr-3 file:rounded file:border-0 file:bg-yellow-400 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-black"
          />
        </div>
      </div>
      <div className="mt-3">
        <FgToggle
          label="Mark as free preview"
          checked={isFreePreview}
          onChange={setIsFreePreview}
        />
        {existingCount === 0 && !isFreePreview && (
          <p className="mt-1 text-xs text-warning">
            ⚠ Heuristic: this will be lesson #1 of a brand-new level — DNA convention
            says it should be a free preview.
          </p>
        )}
      </div>
      <div className="mt-4 flex justify-end">
        <FgButton onClick={createAndUpload} loading={pending}>
          <CloudUpload size={16} /> Upload + encode
        </FgButton>
      </div>
    </FgCard>
  );
}

function LessonRow({
  lesson,
  onToggleFree,
  onRefresh,
  onEdit,
  onDelete,
  dragHandle,
  isDragging,
}: {
  lesson: Lesson;
  onToggleFree: (v: boolean) => void;
  onRefresh: () => void;
  onEdit: () => void;
  onDelete: () => void;
  dragHandle: React.ReactNode;
  isDragging: boolean;
}) {
  return (
    <FgCard className="flex items-center justify-between p-4 fg-anim-fast">
      <div className="flex items-center gap-1">
        {dragHandle}
        <div className="ml-1 flex-1">
          <div className="flex items-center gap-3">
            <span className="rounded bg-black px-2 py-0.5 text-xs font-bold text-yellow-400">
              #{lesson.orderIndex}
            </span>
            <span className="text-sm font-semibold text-black">{lesson.title}</span>
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[lesson.encodingStatus]}`}>
              {lesson.encodingStatus}
            </span>
            {isDragging && (
              <span className="rounded bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-500">
                SAVING…
              </span>
            )}
          </div>
          {lesson.description && <p className="mt-1 text-xs text-gray-600">{lesson.description}</p>}
          {lesson.encodingError && <p className="mt-1 text-xs text-error">⚠ {lesson.encodingError}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onRefresh}
          className="rounded-md p-2 text-[#8E8E93] hover:bg-[#1C1C1E] fg-anim-fast"
          title="Refresh"
        >
          <RefreshCw size={16} />
        </button>
        <FgToggle label="Free" checked={lesson.isFreePreview} onChange={onToggleFree} />
        <FgButton size="sm" variant="outline" onClick={onEdit}>
          <Pencil size={14} /> Edit
        </FgButton>
        <FgButton
          size="sm"
          variant="ghost"
          onClick={onDelete}
          aria-label={`Delete ${lesson.title}`}
        >
          <Trash2 size={14} />
        </FgButton>
      </div>
    </FgCard>
  );
}

function LessonDialog({
  lesson,
  busy,
  onClose,
  onSubmit,
}: {
  lesson: Lesson;
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Partial<Lesson>) => void;
}) {
  const [title, setTitle] = useState(lesson.title);
  const [description, setDescription] = useState(lesson.description ?? '');
  const [orderIndex, setOrderIndex] = useState(String(lesson.orderIndex));
  const [isFreePreview, setIsFreePreview] = useState(lesson.isFreePreview);

  return (
    <FgDialog
      open
      size="lg"
      title={`Edit lesson #${lesson.orderIndex}`}
      description="Lesson metadata only — to re-encode the video, upload a new MP4 from the form above."
      onClose={onClose}
      footer={
        <>
          <FgButton variant="ghost" onClick={onClose}>
            Cancel
          </FgButton>
          <FgButton
            onClick={() =>
              onSubmit({
                title,
                description: description || null,
                orderIndex: Number(orderIndex) || 1,
                isFreePreview,
              })
            }
            loading={busy}
          >
            Save changes
          </FgButton>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            title,
            description: description || null,
            orderIndex: Number(orderIndex) || 1,
            isFreePreview,
          });
        }}
      >
        <FgInput label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <FgInput
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What does this lesson cover?"
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FgInput
            label="Order index"
            type="number"
            value={orderIndex}
            onChange={(e) => setOrderIndex(e.target.value)}
          />
          <div className="flex items-end pb-1">
            <FgToggle
              label="Mark as free preview"
              checked={isFreePreview}
              onChange={setIsFreePreview}
            />
          </div>
        </div>
      </form>
    </FgDialog>
  );
}

async function uploadWithProgress(url: string, file: File): Promise<void> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(url, {
    method: 'POST',
    body: fd,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('fg_admin_access_token') ?? ''}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Upload failed: ${res.status}`);
  }
}
